import { startCase } from 'es-toolkit';
import React from 'react';
import SettingsIcon from '@mui/icons-material/Settings';

import { SelectableEntryOfType, SelectableItemEntry } from '../../../../library/selection/types';
import { showSettingsPanel } from '../../../../store/actions/appActions';
import { selectEntry } from '../../../../store/actions/selectActions';
import { AsyncThunkActionVoid } from '../../../../store/actions/thunkActions';
import { isBlockingOperationOngoing as isBlockingOperationOngoingSelector } from '../../../../store/selectors/blockingOperationSelectors';
import { getSelectedItemEntries } from '../../../../store/selectors/getSelectedItemSelector';
import { inViewOnlyMode } from '../../../../store/selectors/viewOnlyModeSelectors';
import { useAppDispatch, useAppSelector, useIsClientInMobileMode } from '../../../hooks';
import CascadingSceneMenuItem from '../common/CascadingSceneMenuItem';
import { SceneMenuItem } from '../common/SceneMenuItem';

interface SettingsOperation {
  readonly key: string;
  readonly text: string;

  readonly testId?: string;
  readonly disabled?: boolean;

  readonly getActionToDispatch: () => AsyncThunkActionVoid;
}

export function useSettingsOperations(entry: SelectableItemEntry | null) {
  const mobile = useIsClientInMobileMode();
  const isViewOnly = useAppSelector(inViewOnlyMode);

  const isBlockingOperationOngoing = useAppSelector(isBlockingOperationOngoingSelector);

  if (entry == null) return [];

  const isLayoutModelWithSettings = entry.type !== 'componentDesign';

  let result: SettingsOperation[] = [
    {
      key: 'load-settings',
      text: 'Load Settings',
      getActionToDispatch: () => async (dispatch, getState) => {
        const selectedItemEntries = getSelectedItemEntries(getState());
        dispatch(selectEntry(entry));
        dispatch(showSettingsPanel('loadSettings', mobile, selectedItemEntries));
      },
    },
    {
      key: 'analysis-settings',
      text: 'Analysis Settings',
      getActionToDispatch: () => async (dispatch, getState) => {
        const selectedItemEntries = getSelectedItemEntries(getState());
        dispatch(selectEntry(entry));
        dispatch(showSettingsPanel('analysisSettings', mobile, selectedItemEntries));
      },
    },
    {
      key: 'material-settings',
      testId: 'settings-dropdown-material-settings',
      disabled: isViewOnly,
      text: 'Material Settings',
      getActionToDispatch: () => async (dispatch, getState) => {
        const selectedItemEntries = getSelectedItemEntries(getState());
        dispatch(selectEntry(entry));
        dispatch(showSettingsPanel('materialSettings', mobile, selectedItemEntries));
      },
    },
  ];

  if (isLayoutModelWithSettings) {
    result.push({
      key: 'feature-pattern-settings',
      text: 'Feature Pattern Settings',
      getActionToDispatch: () => async (dispatch, getState) => {
        const selectedItemEntries = getSelectedItemEntries(getState());
        dispatch(selectEntry(entry));
        dispatch(showSettingsPanel('featurePatternSettings', mobile, selectedItemEntries));
      },
    });

    result.push({
      key: 'profile-settings',
      text: 'Default Profile Settings',
      getActionToDispatch: () => async (dispatch, getState) => {
        const selectedItemEntries = getSelectedItemEntries(getState());
        dispatch(selectEntry(entry));
        dispatch(showSettingsPanel('profileDefaults', mobile, selectedItemEntries));
      },
    });
  }

  if (isBlockingOperationOngoing) {
    result = result.map((obj) => ({ ...obj, disabled: true }));
  }

  return result;
}

export function SettingsSubMenu(props: {
  readonly entry:
    | SelectableEntryOfType<'project'>
    | SelectableEntryOfType<'building'>
    | SelectableEntryOfType<'level'>
    | SelectableEntryOfType<'componentDesign'>;
  readonly userVisibleTerm?: string;
}) {
  const dispatch = useAppDispatch();
  const operations = useSettingsOperations(props.entry);

  if (operations.length === 0) return null;

  return (
    <>
      <CascadingSceneMenuItem
        icon={<SettingsIcon />}
        label={`${props.userVisibleTerm ?? startCase(props.entry.type)} Settings`}
      >
        {operations.map((operation) => (
          <SceneMenuItem
            key={operation.key}
            text={operation.text}
            data-testid={operation.testId}
            disabled={operation.disabled}
            onClick={() => dispatch(operation.getActionToDispatch())}
          />
        ))}
      </CascadingSceneMenuItem>
    </>
  );
}
